import type { Id, TaskGroup } from '../../../../api/types';
import type { Node, setupPlugins } from './plugins';
import { TaskGroupNode } from './task-group-node';
import type { TaskNode } from './task-node';

type Area = ReturnType<typeof setupPlugins>['area'];
type Editor = ReturnType<typeof setupPlugins>['editor'];

type GroupViewsOptions = {
  area: Area;
  editor: Editor;
  taskNodes: Map<number, TaskNode>;
  connectionMap: Map<number, any>;
  getWorkflowId: () => Id | undefined;
  deleteTaskGroup: (workflowId: Id, groupId: number) => Promise<void>;
  emitWorkflowModified: () => void;
};

export function createGroupViews({
  area,
  editor,
  taskNodes,
  connectionMap,
  getWorkflowId,
  deleteTaskGroup,
  emitWorkflowModified,
}: GroupViewsOptions) {
  let currentTaskGroups: TaskGroup[] = [];
  const groupNodes = new Map<string | number, TaskGroupNode>();
  const groupDragStart = new Map<string | number, { x: number; y: number }>();
  const selectedGroupKeys = new Set<string | number>();
  const groupVisible = new Map<string | number, boolean>();
  const groupContentVisible = new Map<string | number, boolean>();

  const applyGroupSelectionToViews = () => {
    for (const view of area.nodeViews.values()) {
      if (!view?.element) continue;
      const idStr = String((view as any).id ?? '');
      if (!idStr.startsWith('group-')) continue;
      const rawKey = idStr.slice('group-'.length);
      const numKey = Number(rawKey);
      const possibleKeys = [
        rawKey,
        Number.isFinite(numKey) ? numKey : undefined,
        (view as any).id,
      ].filter((k) => k !== undefined) as Array<string | number>;
      const kvEl =
        view.element.shadowRoot?.querySelector('kv-task-group') ??
        view.element.querySelector('kv-task-group');
      const isSelected =
        possibleKeys.findIndex((k) => selectedGroupKeys.has(k as any)) !== -1;
      view.element.classList.toggle('selected', isSelected);
      const node =
        groupNodes.get(numKey as any) ??
        groupNodes.get(rawKey as any) ??
        groupNodes.get((view as any).id as any);
      if (node) {
        (node as any).selected = isSelected;
      }
      if (kvEl) {
        kvEl.toggleAttribute('selected', isSelected);
        kvEl.classList.toggle('selected', isSelected);
      }
    }
  };

  const buildGroupMap = () => {
    const map = new Map<string | number, TaskGroup>();
    (currentTaskGroups ?? []).forEach((g, idx) => {
      const key = (g.id ?? `g-${idx}`) as string | number;
      map.set(key, g);
    });
    return map;
  };

  const getAllTaskIdsForGroup = (
    group: TaskGroup,
    groupMap: Map<string | number, TaskGroup>,
    visited = new Set<string | number>()
  ): number[] => {
    const key =
      (group.id as string | number | undefined) ??
      ((group as any)?._kvKey as string | number | undefined);
    if (key !== undefined) {
      if (visited.has(key)) return [];
      visited.add(key);
    }

    const directTasks =
      group.taskIds ??
      (group as any)?.tasks
        ?.map?.((t: any) => Number(t?.id))
        .filter(Number.isFinite) ??
      [];

    const childIds: Array<string | number> = [
      ...((group.childGroupIds as any as Array<string | number> | undefined) ??
        []),
      ...((group as any)?.childGroups?.map?.((cg: any) => cg?.id) ?? []),
    ].filter((id) => id !== undefined && id !== null);

    const collected = new Set<number>();
    for (const id of directTasks) {
      if (Number.isFinite(id)) collected.add(Number(id));
    }
    for (const childId of childIds) {
      const childKey = childId as string | number;
      const child = groupMap.get(childKey);
      if (child) {
        getAllTaskIdsForGroup(child, groupMap, visited).forEach((tid) => {
          collected.add(tid);
        });
      }
    }
    return Array.from(collected);
  };

  const getDirectTaskIdsForGroup = (group: TaskGroup): number[] =>
    group.taskIds ??
    (group as any)?.tasks
      ?.map?.((t: any) => Number(t?.id))
      .filter(Number.isFinite) ??
    [];

  const removeGroupNode = async (groupKey: string | number) => {
    const node = groupNodes.get(groupKey);
    if (!node) return;
    groupNodes.delete(groupKey);
    try {
      await editor.removeNode(node.id);
    } catch {
      // ignore remove errors
    }
  };

  const ungroupTaskGroup = async (groupKey: string | number) => {
    const workflowId = getWorkflowId();
    if (!workflowId) return;
    const numericId = Number(groupKey);
    if (!Number.isFinite(numericId)) return;
    try {
      await deleteTaskGroup(workflowId, numericId);
      currentTaskGroups = (currentTaskGroups ?? []).filter((g, idx) => {
        const key = (g.id ?? `g-${idx}`) as string | number;
        return key !== groupKey;
      });
      groupVisible.delete(groupKey);
      groupContentVisible.delete(groupKey);
      await removeGroupNode(groupKey);
      await renderTaskGroups();
      updateVisibility();
      emitWorkflowModified();
    } catch (err) {
      console.error('Failed to ungroup task group', err);
    }
  };

  const updateVisibility = () => {
    const parentMap = new Map<string | number, Set<string | number>>();
    (currentTaskGroups ?? []).forEach((group, idx) => {
      const key = (group.id ?? `g-${idx}`) as string | number;
      const children: Array<string | number> = [
        ...((group.childGroupIds as any as
          | Array<string | number>
          | undefined) ?? []),
        ...((group as any)?.childGroups?.map?.((cg: any) => cg?.id) ?? []),
      ].filter((id) => id !== undefined && id !== null);
      children.forEach((child) => {
        const set = parentMap.get(child) ?? new Set();
        set.add(key);
        parentMap.set(child, set);
      });
    });

    const visibleCache = new Map<string | number, boolean>();
    const contentCache = new Map<string | number, boolean>();

    const isGroupVisibleDeep = (
      key: string | number,
      seen = new Set<string | number>()
    ): boolean => {
      const cached = visibleCache.get(key);
      if (cached !== undefined) return cached;
      if (seen.has(key)) return false;
      seen.add(key);
      const ownVisible = groupVisible.get(key) ?? true;
      if (!ownVisible) {
        visibleCache.set(key, false);
        return false;
      }
      const parents = parentMap.get(key);
      if (!parents || parents.size === 0) {
        visibleCache.set(key, true);
        return true;
      }
      const result = [...parents].every((p) => {
        const parentVisible = groupVisible.get(p) ?? true;
        const parentContent = groupContentVisible.get(p) ?? false;
        return parentVisible && parentContent && isGroupVisibleDeep(p, seen);
      });
      visibleCache.set(key, result);
      return result;
    };

    const isGroupContentVisibleDeep = (
      key: string | number,
      seen = new Set<string | number>()
    ): boolean => {
      const cached = contentCache.get(key);
      if (cached !== undefined) return cached;
      if (seen.has(key)) return false;
      seen.add(key);
      const ownVisible = groupVisible.get(key) ?? true;
      const ownContent = groupContentVisible.get(key) ?? false;
      if (!(ownVisible && ownContent)) {
        contentCache.set(key, false);
        return false;
      }
      const parents = parentMap.get(key);
      if (!parents || parents.size === 0) {
        contentCache.set(key, true);
        return true;
      }
      const result = [...parents].every((p) => {
        const parentVisible = groupVisible.get(p) ?? true;
        const parentContent = groupContentVisible.get(p) ?? false;
        return (
          parentVisible && parentContent && isGroupContentVisibleDeep(p, seen)
        );
      });
      contentCache.set(key, result);
      return result;
    };

    const groupNodeIds = new Set(
      Array.from(groupNodes.values()).map((n) => String(n.id))
    );
    const groupedByTask = new Map<number, Set<string | number>>();
    currentTaskGroups?.forEach((group, idx) => {
      const key = (group.id ?? `g-${idx}`) as string | number;
      if (!groupContentVisible.has(key)) {
        groupContentVisible.set(key, false);
      }
      if (!groupVisible.has(key)) {
        groupVisible.set(key, true);
      }
      for (const id of getDirectTaskIdsForGroup(group)) {
        if (!Number.isFinite(id)) continue;
        const set = groupedByTask.get(id) ?? new Set();
        set.add(key);
        groupedByTask.set(id, set);
      }
    });

    for (const [id, view] of area.nodeViews) {
      if (groupNodeIds.has(String(id))) {
        if (view?.element) {
          const key = String(id).startsWith('group-')
            ? String(id).slice('group-'.length)
            : undefined;
          const parsedKey =
            key && Number.isFinite(Number(key)) ? Number(key) : key;
          const visible =
            parsedKey != null ? isGroupVisibleDeep(parsedKey as any) : true;
          view.element.style.visibility = visible ? 'visible' : 'hidden';
          view.element.style.display = visible ? 'block' : 'none';
        }
        continue;
      }
      const numId = Number(id);
      const el = view?.element as HTMLElement | undefined;
      if (!el) continue;
      const groups = groupedByTask.get(numId);
      if (!groups || groups.size === 0) {
        el.style.visibility = 'visible';
        continue;
      }
      const show = [...groups].some((g) => isGroupContentVisibleDeep(g));
      el.style.visibility = show ? 'visible' : 'hidden';
    }

    for (const [_apiId, conn] of connectionMap) {
      const fromId = (conn as any)._kvFromId as number | undefined;
      const toId = (conn as any)._kvToId as number | undefined;
      const view = area.connectionViews.get((conn as any).id as any);
      if (!view?.element) continue;
      const fromGroups = fromId != null ? groupedByTask.get(fromId) : undefined;
      const toGroups = toId != null ? groupedByTask.get(toId) : undefined;
      const shareGroup =
        fromGroups && toGroups
          ? [...fromGroups].some((g) => toGroups.has(g))
          : false;
      if (!shareGroup) {
        view.element.style.visibility = 'visible';
        continue;
      }
      const combinedGroups = new Set<string | number>([
        ...(fromGroups ?? []),
        ...(toGroups ?? []),
      ]);
      const show = [...combinedGroups].some((g) =>
        isGroupContentVisibleDeep(g)
      );
      view.element.style.visibility = show ? 'visible' : 'hidden';
    }
  };

  const renderTaskGroups = async () => {
    const padding = 24;
    const groupMap = buildGroupMap();
    const parentMap = new Map<string | number, Set<string | number>>();
    (currentTaskGroups ?? []).forEach((group, idx) => {
      const key = (group.id ?? `g-${idx}`) as string | number;
      const children: Array<string | number> = [
        ...((group.childGroupIds as any as
          | Array<string | number>
          | undefined) ?? []),
        ...((group as any)?.childGroups?.map?.((cg: any) => cg?.id) ?? []),
      ].filter((id) => id !== undefined && id !== null);
      children.forEach((child) => {
        const set = parentMap.get(child) ?? new Set();
        set.add(key);
        parentMap.set(child, set);
      });
    });
    const depthCache = new Map<string | number, number>();
    const getDepth = (
      key: string | number,
      seen = new Set<string | number>()
    ): number => {
      const cached = depthCache.get(key);
      if (cached !== undefined) return cached;
      if (seen.has(key)) return 0;
      seen.add(key);
      const parents = parentMap.get(key);
      if (!parents || parents.size === 0) {
        depthCache.set(key, 0);
        return 0;
      }
      const depth = 1 + Math.max(...[...parents].map((p) => getDepth(p, seen)));
      depthCache.set(key, depth);
      return depth;
    };
    const activeKeys = new Set<string | number>();
    try {
      for (const [idx, group] of (currentTaskGroups ?? []).entries()) {
        const ids = getAllTaskIdsForGroup(group, groupMap);
        if (!ids.length) {
          continue;
        }
        const boxes = ids
          .map((taskId) => {
            const node = taskNodes.get(taskId);
            const view = area.nodeViews.get(String(taskId));
            if (!node || !view || !view?.position) return null;
            const width =
              (node as any).width ??
              view.element?.clientWidth ??
              (node as any)?.meta?.width ??
              200;
            const height =
              (node as any).height ??
              (node as any)?.meta?.height ??
              view.element?.clientHeight ??
              200;
            return {
              x: view.position.x,
              y: view.position.y,
              width,
              height,
            };
          })
          .filter(Boolean) as {
          x: number;
          y: number;
          width: number;
          height: number;
        }[];
        if (boxes.length === 0) {
          continue;
        }
        const minX = Math.min(...boxes.map((b) => b.x)) - padding;
        const minY = Math.min(...boxes.map((b) => b.y)) - padding;
        const maxX = Math.max(...boxes.map((b) => b.x + b.width)) + padding;
        const maxY = Math.max(...boxes.map((b) => b.y + b.height)) + padding;
        const groupKey = (group.id ?? `g-${idx}`) as string | number;
        activeKeys.add(groupKey);
        if (!groupContentVisible.has(groupKey)) {
          groupContentVisible.set(groupKey, false);
        }
        if (!groupVisible.has(groupKey)) {
          groupVisible.set(groupKey, true);
        }

        let groupNode = groupNodes.get(groupKey);
        if (!groupNode) {
          groupNode = new TaskGroupNode(
            groupKey,
            group.title ?? `Group ${groupKey}`
          );
          groupNodes.set(groupKey, groupNode);
          await editor.addNode(groupNode as unknown as Node);
        }
        const currentGroupNode = groupNode;
        currentGroupNode.title = group.title ?? `Group ${groupKey}`;
        currentGroupNode.taskCount = boxes.length;
        currentGroupNode.visible = !!groupVisible.get(groupKey);
        currentGroupNode.contentVisible = !!groupContentVisible.get(groupKey);
        currentGroupNode.toggleContentVisibility = () => {
          const next = !(groupContentVisible.get(groupKey) ?? false);
          groupContentVisible.set(groupKey, next);
          currentGroupNode.contentVisible = next;
          void area.update('node', currentGroupNode.id);
          const view = area.nodeViews.get(currentGroupNode.id);
          const kvEl = view?.element?.querySelector?.(
            'kv-task-group'
          ) as HTMLElement | null;
          kvEl?.toggleAttribute('content-visible', currentGroupNode.contentVisible);
          kvEl?.classList.toggle(
            'content-visible',
            currentGroupNode.contentVisible
          );
          updateVisibility();
        };
        currentGroupNode.toggleGroupVisibility = () => {
          const next = !(groupVisible.get(groupKey) ?? true);
          groupVisible.set(groupKey, next);
          currentGroupNode.visible = next;
          void area.update('node', currentGroupNode.id);
          const view = area.nodeViews.get(currentGroupNode.id);
          if (view?.element) {
            view.element.style.display = currentGroupNode.visible
              ? 'block'
              : 'none';
            view.element.style.visibility = currentGroupNode.visible
              ? 'visible'
              : 'hidden';
          }
          updateVisibility();
        };
        currentGroupNode.ungroup = () => {
          void ungroupTaskGroup(groupKey);
        };
        currentGroupNode.width = maxX - minX;
        currentGroupNode.height = maxY - minY;
        await area.translate(currentGroupNode.id, { x: minX, y: minY });
        await area.update('node', currentGroupNode.id);
        const view = area.nodeViews.get(currentGroupNode.id);
        if (view?.element) {
          const elStyle = view.element.style;
          elStyle.width = `${currentGroupNode.width}px`;
          elStyle.height = `${currentGroupNode.height}px`;
          elStyle.pointerEvents = 'auto';
          elStyle.background = 'transparent';
          elStyle.border = 'none';
          // Allow clicking labels; keep transparent so tasks remain visible
          const depth = getDepth(groupKey);
          const baseZ = -5; // keep groups behind tasks but allow children to stack above parents
          elStyle.zIndex = String(baseZ + depth);
          elStyle.display = currentGroupNode.visible ? 'block' : 'none';
          elStyle.visibility = currentGroupNode.visible ? 'visible' : 'hidden';
          elStyle.overflow = 'visible';
          elStyle.transform = view.element.style.transform; // keep Rete transform
          // Ensure visibility attribute is in sync for styling/debug
          const kvEl =
            view.element.shadowRoot?.querySelector('kv-task-group') ??
            view.element.querySelector('kv-task-group');
          if (kvEl) {
            kvEl.toggleAttribute(
              'content-visible',
              !!currentGroupNode.contentVisible
            );
            kvEl.classList.toggle(
              'content-visible',
              !!currentGroupNode.contentVisible
            );
          }
        }
      }
      // Remove group nodes that no longer exist
      for (const [key, node] of Array.from(groupNodes.entries())) {
        if (!activeKeys.has(key)) {
          groupNodes.delete(key);
          try {
            void editor.removeNode(node.id);
          } catch {
            // ignore
          }
        }
      }
      applyGroupSelectionToViews();
      updateVisibility();
    } catch (err) {
      console.error('[workflow] renderTaskGroups failed', err);
    }
  };

  const handlePipe = (context: any) => {
    if (context.type === 'nodepicked' && context.data?.id) {
      const node = editor.getNode?.(context.data.id as any);
      if (node instanceof TaskGroupNode) {
        const view = area.nodeViews.get(String(node.id));
        if (view) {
          groupDragStart.set(node.id, { ...view.position });
        }
      }
    }
    if (context.type === 'nodedragged') {
      const node = (context as any).data as any;
      if (node instanceof TaskGroupNode) {
        const start = groupDragStart.get(node.id);
        const view = area.nodeViews.get(String(node.id));
        if (start && view) {
          void area.translate(node.id, start);
          void area.update('node', node.id);
        }
        return context;
      }
    }
    return context;
  };

  const setTaskGroups = async (groups: TaskGroup[]) => {
    currentTaskGroups = groups ?? [];
    // Remove visibility entries for groups that no longer exist
    const validKeys = new Set(
      (currentTaskGroups ?? []).map(
        (g, idx) => (g.id ?? `g-${idx}`) as string | number
      )
    );
    for (const key of Array.from(groupVisible.keys())) {
      if (!validKeys.has(key)) {
        groupVisible.delete(key);
        groupContentVisible.delete(key);
      }
    }
    // Ensure defaults for new groups (hidden content by default)
    currentTaskGroups?.forEach((g, idx) => {
      const key = (g.id ?? `g-${idx}`) as string | number;
      if (!groupVisible.has(key)) groupVisible.set(key, true);
      if (!groupContentVisible.has(key)) groupContentVisible.set(key, false);
    });
    await renderTaskGroups();
    updateVisibility();
  };

  const setSelectedGroup = (
    groupId?: string | number | Array<string | number>
  ) => {
    selectedGroupKeys.clear();
    if (Array.isArray(groupId)) {
      for (const g of groupId) {
        selectedGroupKeys.add(g);
      }
    } else if (groupId != null) {
      selectedGroupKeys.add(groupId);
    }
    applyGroupSelectionToViews();
  };

  const reset = (groups: TaskGroup[] = []) => {
    currentTaskGroups = groups ?? [];
    groupNodes.clear();
    groupDragStart.clear();
    selectedGroupKeys.clear();
    groupVisible.clear();
    groupContentVisible.clear();
  };

  return {
    handlePipe,
    renderTaskGroups,
    updateVisibility,
    setTaskGroups,
    setSelectedGroup,
    reset,
  };
}
